import React, { useState, useRef } from 'react';
import {
  Sparkles, Upload, X, Loader2, Zap, FileText,
  Image as ImageIcon, AlertCircle, ChevronDown, ChevronUp,
} from 'lucide-react';
import { generateQuoteConfig, analyzeBills } from '../services/geminiService';
import { AIQuoteResponse, MultiCarrierResult } from '../types';

interface Props {
  companyName:          string;
  onQuoteGenerated:     (r: AIQuoteResponse) => void;
  onBillAnalyzed?:      (s: MultiCarrierResult) => void;
  onInsightsGenerated?: (r: AIQuoteResponse) => void;
}

interface UploadedFile { name: string; base64: string; mimeType: string; preview?: string; }

type AIMode = 'generate' | 'analyze';

export const AIGenerator: React.FC<Props> = ({
  companyName, onQuoteGenerated, onBillAnalyzed,
}) => {
  const [mode,        setMode]       = useState<AIMode>('generate');
  const [prompt,      setPrompt]     = useState('');
  const [files,       setFiles]      = useState<UploadedFile[]>([]);
  const [loading,     setLoading]    = useState(false);
  const [error,       setError]      = useState('');
  const [expanded,    setExpanded]   = useState(true);
  const fileRef = useRef<HTMLInputElement>(null);

  const EXAMPLE_PROMPTS = [
    'Construction company with 25 workers needing rugged smartphones and fleet tracking for 12 trucks',
    'Medical office, 8 staff lines with shared tablets for patient check-in and fast internet',
    'Retail chain with 5 locations needing POS internet, 30 lines, and store tablets',
  ];

  const toBase64 = (file: File): Promise<string> =>
    new Promise((res, rej) => {
      const reader = new FileReader();
      reader.onload = () => res((reader.result as string).split(',')[1]);
      reader.onerror = rej;
      reader.readAsDataURL(file);
    });

  const handleFiles = async (fileList: FileList | null) => {
    if (!fileList) return;
    const newFiles: UploadedFile[] = [];
    for (const file of Array.from(fileList)) {
      const base64   = await toBase64(file);
      const preview  = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined;
      newFiles.push({ name: file.name, base64, mimeType: file.type, preview });
    }
    setFiles(prev => [...prev, ...newFiles]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const handleSubmit = async () => {
    if (mode === 'generate' && !prompt.trim() && !files.length) {
      setError('Please describe the customer requirements or upload a bill.');
      return;
    }
    if (mode === 'analyze' && !files.length) {
      setError('Please upload at least one carrier bill to analyze.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      if (mode === 'analyze') {
        const result = await analyzeBills(files);
        if (result && onBillAnalyzed) onBillAnalyzed(result);
      } else {
        const result = await generateQuoteConfig(prompt, companyName, files.length ? files : undefined);
        if (result) onQuoteGenerated(result);
      }
    } catch (err) {
      console.error(err);
      setError('Konektra AI request failed. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl bg-gradient-to-br from-purple-900/30 to-violet-900/20 border border-purple-500/20 overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-5 hover:bg-white/3 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
            <Sparkles size={18} className="text-white" />
          </div>
          <div className="text-left">
            <div className="font-bold text-white text-sm">AI Quote Assistant</div>
            <div className="text-xs text-purple-400">Powered by Konektra AI</div>
          </div>
        </div>
        {expanded ? <ChevronUp size={16} className="text-gray-500" /> : <ChevronDown size={16} className="text-gray-500" />}
      </button>

      {expanded && (
        <div className="px-5 pb-5 space-y-4 animate-fade-in">
          {/* Mode tabs */}
          <div className="flex rounded-xl bg-black/30 p-1 gap-1">
            <button
              onClick={() => setMode('generate')}
              className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold transition-all ${
                mode === 'generate' ? 'bg-purple-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'
              }`}
            >
              <Zap size={14} /> Generate Quote
            </button>
            <button
              onClick={() => setMode('analyze')}
              className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold transition-all ${
                mode === 'analyze' ? 'bg-purple-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'
              }`}
            >
              <FileText size={14} /> Analyze Bill
            </button>
          </div>

          {/* Text input (generate mode) */}
          {mode === 'generate' && (
            <div className="space-y-2">
              <textarea
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                placeholder="Describe the customer's needs... e.g., 'Trucking company, 15 drivers needing rugged devices and GPS tracking, plus office internet'"
                rows={3}
                className="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/20 text-white placeholder-gray-600 text-sm resize-none outline-none transition-all"
              />
              <div className="flex flex-wrap gap-2">
                {EXAMPLE_PROMPTS.map(ex => (
                  <button
                    key={ex}
                    onClick={() => setPrompt(ex)}
                    className="text-[10px] px-2.5 py-1 rounded-lg bg-purple-500/10 border border-purple-500/15 text-purple-400 hover:bg-purple-500/20 transition-colors truncate max-w-[200px]"
                  >
                    {ex.substring(0, 36)}…
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* File upload */}
          <div
            onDrop={handleDrop}
            onDragOver={e => e.preventDefault()}
            onClick={() => fileRef.current?.click()}
            className="relative flex flex-col items-center justify-center p-6 rounded-xl border-2 border-dashed border-white/10 hover:border-purple-500/40 hover:bg-white/3 cursor-pointer transition-all group"
          >
            <input
              ref={fileRef}
              type="file"
              multiple
              accept="image/*,application/pdf"
              className="hidden"
              onChange={e => handleFiles(e.target.files)}
            />
            <Upload size={22} className="text-gray-600 group-hover:text-purple-400 mb-2 transition-colors" />
            <p className="text-xs text-gray-500 text-center">
              {mode === 'analyze'
                ? 'Drop carrier bill images or PDFs here'
                : 'Optionally attach bills or account screenshots'}
            </p>
            <p className="text-[10px] text-gray-700 mt-1">PNG, JPG, PDF supported</p>
          </div>

          {/* File previews */}
          {files.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {files.map((f, i) => (
                <div key={i} className="relative flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-gray-400 max-w-[180px]">
                  {f.preview
                    ? <img src={f.preview} alt="" className="w-6 h-6 rounded object-cover" />
                    : <ImageIcon size={14} className="shrink-0 text-gray-500" />
                  }
                  <span className="truncate">{f.name}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); setFiles(prev => prev.filter((_, j) => j !== i)); }}
                    className="ml-1 text-gray-600 hover:text-red-400 shrink-0"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs">
              <AlertCircle size={14} className="shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-500 hover:to-violet-500 text-white font-bold text-sm transition-all duration-200 shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                {mode === 'analyze' ? 'Analyzing bill...' : 'Generating quote...'}
              </>
            ) : (
              <>
                <Sparkles size={16} />
                {mode === 'analyze' ? 'Analyze with AI' : 'Generate with AI'}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};
